import dayjs from 'dayjs'
import { Form, Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { dateConversions, KeyboardDatePickerInput } from '@dhl/inputs'
import { Box, Button, Card, Divider, Grid, H6 } from '@dhl-ui/components'
import { getDownload } from '~/api/admin-api'
import { useNotificationMessage } from '~/common'
import { createStartDateEndDateValidationSchema } from '../model/validation'

type BusinessMetricsFormValues = {
  interval: {
    from: Date | null
    till: Date | null
  }
}

export const BusinessMetricsReportPage = () => {
  const { t } = useTranslation()
  const { showError } = useNotificationMessage()

  const onSubmit = async (values: BusinessMetricsFormValues) => {
    const startDate = dayjs(values.interval.from).format('YYYY-MM-DD')
    const endDate = dayjs(values.interval.till).format('YYYY-MM-DD')

    /* download URL as per https://dhlbnl.atlassian.net/browse/B2XR-1336 */
    const downloadUrl = `/api/intervention/export?startDate=${startDate}&endDate=${endDate}`
    const response = await getDownload(downloadUrl, `businessMetrics.csv`)
    if (response) {
      showError(JSON.parse(response).message)
    }
  }

  const validationSchema = createStartDateEndDateValidationSchema(t)
  return (
    <>
      <Formik
        initialValues={
          {
            interval: {
              from: null,
              till: null,
            },
          } as BusinessMetricsFormValues
        }
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        validateOnChange={false}
        enableReinitialize
      >
        {({ isSubmitting, handleSubmit }) => (
          <Card>
            <Card.Header title={t('admin.reportsPage.businessMetrics')} />
            <Divider />
            <Card.Content>
              <Form data-testid="business-metrics-form">
                <Grid container spacing={2} direction="row" alignItems="center">
                  <Grid size={{ xs: 2 }}>
                    <H6>{t('admin.reportsPage.dateRange')}</H6>
                  </Grid>
                  <Grid container size={{ xs: 10 }} spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <KeyboardDatePickerInput
                        variant="standard"
                        label={t('admin.reportsPage.startDate')}
                        name="interval.from"
                        conversions={dateConversions}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <KeyboardDatePickerInput
                        variant="standard"
                        label={t('admin.reportsPage.endDate')}
                        name="interval.till"
                        conversions={dateConversions}
                        fullWidth
                        disableFuture={false}
                      />
                    </Grid>
                  </Grid>
                  <Grid container size={12} sx={{ mt: 4, justifyContent: 'flex-end' }}>
                    <Button
                      size="medium"
                      disabled={isSubmitting}
                      onClick={() => {
                        handleSubmit()
                      }}
                      color="primary"
                      type="button"
                      variant="contained"
                    >
                      {t('admin.reportsPage.downloadBusinessMetrics')}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            </Card.Content>
          </Card>
        )}
      </Formik>
    </>
  )
}
